import React from 'react';
import { mockHistory } from './../data/mockData';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Clock, User, Loader2, History as HistoryIcon } from 'lucide-react';

const actionLabels = {
  created: 'Criado',
  updated: 'Atualizado',
  deleted: 'Excluído',
  status_changed: 'Status alterado',
  assignee_changed: 'Responsável alterado'
};

const actionColors = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
  status_changed: 'bg-amber-100 text-amber-700',
  assignee_changed: 'bg-purple-100 text-purple-700'
};

export default function TaskHistoryModal({ taskId, taskTitle, open, onClose }) {
  const isLoading = false;
  const history = mockHistory.filter(h => h.task_id === taskId);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4 border-b">
            <div className="flex items-center gap-2">
              <HistoryIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold">
                Histórico da Tarefa
              </h2>
            </div>
            {taskTitle && (
              <p className="text-sm text-slate-600 mt-2">"{taskTitle}"</p>
            )}
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[60vh] p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <HistoryIcon className="w-12 h-12 mb-3" />
                <p className="text-sm">Nenhum histórico encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-all"
                  >
                    <div className="flex-shrink-0 pt-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`${actionColors[item.action] || 'bg-slate-100 text-slate-700'} font-semibold px-2 py-1 rounded text-xs`}>
                          {actionLabels[item.action] || item.action}
                        </span>
                        {item.field_changed && (
                          <span className="text-xs text-slate-500">
                            • {item.field_changed}
                          </span>
                        )}
                      </div>
                      
                      {(item.old_value || item.new_value) && (
                        <div className="text-sm mb-2 space-y-1">
                          {item.old_value && (
                            <div className="text-slate-600">
                              <span className="font-medium text-slate-700">De:</span> {item.old_value}
                            </div>
                          )}
                          {item.new_value && (
                            <div className="text-slate-600">
                              <span className="font-medium text-slate-700">Para:</span> {item.new_value}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                        {item.changed_by && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {item.changed_by}
                          </span>
                        )}
                        {item.created_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(item.created_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}